import { useState } from 'react';

import { getSynonymSearchApiKey } from 'api/getSynonymSearchApiKey';
import { Suggestion } from 'components/Autocomplete/AutocompleteTextInput';
import { Button } from 'components/Button/Button';
import { FieldLabel } from 'components/Form/Field/FieldLabel';
import { useFormContext } from 'components/Form/FormContext';
import { GeneralError } from 'components/Form/GeneralError';
import { InputField } from 'components/Form/InputField';
import { LocationField } from 'components/Form/LocationField';
import { SubmitButton } from 'components/Form/SubmitButton/SubmitButton';
import { Icon } from 'components/Icon/Icon';
import { LegacyTooltip } from 'components/LegacyTooltip/LegacyTooltip';
import { Text } from 'components/Text/Text';
import { useViewportBreakpoint } from 'hooks/viewport/useViewportBreakpoint';
import { TitleDescriptionModal } from 'modules/salaries/modals/TitleDescription/TitleDescriptionModal';
import { SalariesReportFormData } from 'modules/salaries/types/SalariesReportFormData';
import { searchAlgoliaConfig } from 'modules/search/algolia/searchAlgoliaConfig';
import { RichError } from 'utils/RichError';
import {
  SerializedAddressComponent,
  serializeAddress,
} from 'utils/googleAddresses';
import { trackError } from 'utils/trackError';

import {
  ButtonContainer,
  FieldLabelContainer,
  FormFieldContainer,
  GeneralErrorContainer,
  IconContainer,
  StyledAutocompleteTextInputField,
  SubmitButtonContainer,
  YearsOfExperienceInputContainer,
} from './MainChartSidebarForm.styled';

type Props = {
  searchApiKey: string;
  setSearchApiKey: (searchApiKey: string) => void;
};

export function MainChartSidebarForm({ searchApiKey, setSearchApiKey }: Props) {
  const [showModal, setShowModal] = useState(false);
  const { clearErrors, updateValue, updateValues, updateValueWith, values } =
    useFormContext<SalariesReportFormData>();

  const breakpoint = useViewportBreakpoint();
  const isMobile = breakpoint === 'sm' || breakpoint === 'xs';

  const selectAddress = ({
    city,
    geoType,
    stateCode,
    latitude,
    longitude,
  }: SerializedAddressComponent) => {
    updateValueWith(
      ['geoType', 'stateCode', 'city', 'latitude', 'longitude'],
      ({
        city: formCity,
        geoType: formGeoType,
        stateCode: formStateCode,
        latitude: formLatitude,
        longitude: formLongitude,
      }) => ({
        city: city || formCity,
        geoType: geoType || formGeoType,
        stateCode: stateCode || formStateCode,
        latitude: latitude || formLatitude,
        longitude: longitude || formLongitude,
        cityStateUnified: city && stateCode && `${city}, ${stateCode}`,
      }),
    );
  };

  const handlePlaceDetails = (place: google.maps.GeocoderResult) => {
    const serializedAddress = serializeAddress(place);
    selectAddress({ ...serializedAddress, geoType: place.types[0] });

    // https://developers.google.com/maps/documentation/javascript/reference/places-widget#Autocomplete.getPlace  -- failure case
    if (!serializedAddress.city) {
      trackError(
        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
        new RichError('Failed to fetch API result', JSON.stringify(place)),
      );
    }
  };

  const suggestionComponentFn = ({ name }: Suggestion) => (
    <Text data-qa-id="autocomplete-suggestion">{name}</Text>
  );

  return (
    <>
      {searchApiKey ? (
        <StyledAutocompleteTextInputField
          autoFocus={!values.jobTitle}
          label={`${getText('Job Title')}*`}
          name="jobTitle"
          placeholder={getText('Program Manager')}
          autocompleteType="SALARY_REPORT_SURVEY_TITLES"
          onChange={(e: { target: { value: string } }) => {
            updateValues({
              jobTitle: e.target.value,
              selectedSynonymId: undefined,
              selectedSynonymFamilyId: undefined,
            });
          }}
          suggestionComponentFn={suggestionComponentFn}
          onSuggestionSelected={(_, payload) => {
            const { value } = payload.suggestion;
            updateValues({
              jobDescription: value.jobTitle.description,
              jobTitle: value.name,
              jobTitleId: value.jobTitle.id,
              selectedSynonymId: value.objectID,
              selectedSynonymFamilyId: value.jobTitle.familyId,
            });
            clearErrors(['selectedSynonymId']);
          }}
          customErrorMatch={(errors) => errors.selectedSynonymId}
          searchConfig={searchAlgoliaConfig(searchApiKey)}
          updateApiKey={() =>
            getSynonymSearchApiKey().then((newApiKey) => {
              setSearchApiKey(newApiKey);
              return newApiKey;
            })
          }
        />
      ) : (
        <InputField
          name="jobTitle"
          label={`${getText('Job Title')}*`}
          placeholder={getText('Program Manager')}
        />
      )}
      {values.selectedSynonymId && (
        <>
          <ButtonContainer>
            <Button
              type="button"
              onClick={() => setShowModal(true)}
              noPadding
              data-qa-id="read-title-description-button"
              variant="link-white"
              size="tiny"
            >
              {getText('Read title description')}
            </Button>
          </ButtonContainer>
          {showModal ? (
            <TitleDescriptionModal
              onClose={() => setShowModal(false)}
              jobDescription={values.jobDescription}
              jobTitle={values.jobTitle}
            />
          ) : null}
        </>
      )}
      <FormFieldContainer>
        <FieldLabelContainer>
          <FieldLabel>{getText('Location')}</FieldLabel>
        </FieldLabelContainer>
      </FormFieldContainer>
      <LocationField
        name="cityStateUnified"
        addressTypes={['(cities)']}
        placeholder={getText('e.g. Hawkins, IN')}
        componentRestrictions={{
          country: 'US',
        }}
        handlePlaceDetails={handlePlaceDetails}
        locationText={values.cityStateUnified}
        onClear={() =>
          updateValueWith(
            ['geoType', 'city', 'stateCode', 'latitude', 'longitude'],
            () => ({
              geoType: null,
              city: null,
              stateCode: null,
              latitude: null,
              longitude: null,
            }),
          )
        }
        customErrorMatch={(errors) => errors.stateCode}
      />

      <FormFieldContainer>
        <FieldLabelContainer>
          <FieldLabel>{getText('Years of Relevant Experience')}</FieldLabel>
        </FieldLabelContainer>
        <LegacyTooltip green lightColor>
          {getText(
            `Includes experience in the role listed above and any time spent in similar/transferable roles. For example: if you have 2 years of experience in fundraising and 1 in social work—and the role above is in fundraising—you'd have 2 years of relevant experience.`,
          )}
        </LegacyTooltip>
      </FormFieldContainer>
      <YearsOfExperienceInputContainer>
        <InputField
          autoComplete="off"
          name="yearsOfExperience"
          type="number"
          step={1}
          min={0}
          onChange={(e) =>
            updateValue('yearsOfExperience', Number(e.target.value))
          }
        />
      </YearsOfExperienceInputContainer>

      <GeneralErrorContainer>
        <GeneralError />
      </GeneralErrorContainer>

      <SubmitButtonContainer>
        <SubmitButton
          size="small"
          fullWidth={isMobile}
          disabled={!searchApiKey}
          variant="secondary-white"
        >
          <Text styleAs="h5">{getText("Let's Go")}</Text>
        </SubmitButton>
      </SubmitButtonContainer>

      <IconContainer>
        <Icon colorName="white" name="arrow-down" size={24} />
      </IconContainer>
    </>
  );
}
